import {
  Box,
  FormControl,
  MenuItem,
  MenuList,
  Popover,
  Skeleton,
  Typography
} from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import React, { useRef, useState } from 'react'
import LinkIcon from '../../../../../assets/icons/link.svg?component'
import CalendarIcon from '../../../../../assets/icons/calendar.svg?component'
import { DayDataTypes } from '../../../../../api/models/dataList.model'
import { RestrictionTypeList } from '../../../../../api/models/restrictionTypeList.model'
import { useTranslation } from 'react-i18next'
import { HtmlTooltip } from 'src/components/htmlTooltip/HTMLTooltip'
import { TooltipChildren } from 'src/components/tooltipChildren/TooltipChildren'
import { ArrowDropDown } from '@mui/icons-material'
import {
  RatesDataGridRow,
  RowType
} from '../contexts/ratesDataGrid/RatesDataGridContext.types'

export type DataTypeChangeResult =
  | 'applied'
  | 'unchanged'
  | 'confirmation-required'

interface Props {
  cellParams: GridRenderCellParams
  openCreateRatePriceModal: (row: any) => void
  restrictionTypeList: RestrictionTypeList[]
  onChangeDataType: (
    row: RatesDataGridRow,
    dataType: keyof DayDataTypes
  ) => DataTypeChangeResult
}

const RatesGridCol2CellRender: ReactFC<Props> = ({
  cellParams: { row, id, value, tabIndex, api },
  openCreateRatePriceModal,
  restrictionTypeList,
  onChangeDataType
}) => {
  const anchorEl = useRef<HTMLDivElement>(null)
  const preservedScrollTop = useRef<number | null>(null)
  const shouldRestoreSelectorFocus = useRef(true)
  const rowData = row as RatesDataGridRow
  const { t } = useTranslation()
  const [openDropdown, setOpenDropdown] = useState(false)
  const [showMore, setShowMore] = useState(false)
  if (rowData.type.includes('Skeleton')) {
    return <Skeleton variant='rectangular' width='100%' height='60%' />
  }
  if (
    rowData.type !== RowType.RATE &&
    rowData.type !== RowType.CHANNEL &&
    rowData.type !== RowType.CAPACITY
  ) {
    return value ?? null
  }

  const handleClose = () => {
    setOpenDropdown(false)
    setShowMore(false)
  }

  const handleChangeDataType = (val: keyof DayDataTypes) => {
    preservedScrollTop.current = api.getScrollPosition().top
    shouldRestoreSelectorFocus.current =
      onChangeDataType(rowData, val) !== 'confirmation-required'
    handleClose()
  }

  const handlePopoverExited = () => {
    if (shouldRestoreSelectorFocus.current) {
      anchorEl.current?.focus({ preventScroll: true })
    }

    if (preservedScrollTop.current != null) {
      api.scroll({ top: preservedScrollTop.current })
    }

    preservedScrollTop.current = null
    shouldRestoreSelectorFocus.current = true
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between'
      }}
    >
      <FormControl
        sx={{
          width: '80%'
        }}
      >
        <Box
          sx={{
            cursor: 'pointer',
            display: 'flex'
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setOpenDropdown(true)
          }}
          ref={anchorEl}
          role='button'
          tabIndex={tabIndex}
          aria-expanded={openDropdown}
          aria-haspopup='menu'
        >
          <Typography
            sx={{
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              wordWrap: 'break-word',
              whiteSpace: 'nowrap'
            }}
          >
            {restrictionTypeList.find(
              (restriction) => restriction.id === rowData.dayDataType
            )?.name || t('labels.Prices')}
          </Typography>
          <ArrowDropDown />
        </Box>
        <Popover
          anchorEl={anchorEl.current}
          disableRestoreFocus
          open={openDropdown}
          onClose={() => handleClose()}
          TransitionProps={{
            onExited: handlePopoverExited
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          <MenuList>
            <MenuItem
              value='price'
              onClick={() => {
                handleChangeDataType('price')
              }}
            >
              {t('labels.Prices')}
            </MenuItem>
            {(showMore
              ? restrictionTypeList
              : restrictionTypeList.slice(0, 3)
            )?.map((restriction) => (
              <MenuItem
                key={`${id}-${restriction.id}`}
                onClick={() => {
                  handleChangeDataType(restriction.id)
                }}
              >
                {restriction.name}
              </MenuItem>
            ))}
            {!showMore && (
              <MenuItem
                onClick={() => {
                  setShowMore(true)
                }}
              >
                Mostrar más
              </MenuItem>
            )}
          </MenuList>
        </Popover>
      </FormControl>
      <Box
        onClick={() => {
          if (rowData.inherited) return
          openCreateRatePriceModal(row)
        }}
        style={{
          width: '20%',
          height: '1rem',
          marginRight: '.5rem',
          cursor: rowData.inherited ? 'default' : 'pointer'
        }}
      >
        {rowData.inherited ? (
          <HtmlTooltip
            title={
              <ul>
                {rowData.inheritedDataList?.map((inheritedData, index) => (
                  <li key={`${rowData.id}-inheritedData-${index}`}>
                    {inheritedData}
                  </li>
                ))}
              </ul>
            }
          >
            <TooltipChildren>
              <LinkIcon />
            </TooltipChildren>
          </HtmlTooltip>
        ) : (
          <CalendarIcon />
        )}
      </Box>
    </Box>
  )
}
export default React.memo(RatesGridCol2CellRender)
